import classNames from './GoogleClassesModal.module.scss';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Transition } from 'react-transition-group';
import TheButton from '@/components/TheButton/TheButton';
import AppBackdrop from '@/components/AppBackdrop';
import AppModal from '@/components/AppModal';
import { BaseGoogleCourses } from '@/types/register';
import { useEffect, useState } from 'react';
import SVG from 'react-inlinesvg';
import closeIcon from '@/assets/svg/closeFill.svg';
import AppLoaderCircle from '../AppLoaderCircle';

interface GoogleClassesModalProps {
  show: boolean;
  classes: BaseGoogleCourses[];
  isLoading: boolean;
  sync?: boolean;
  deleteClasses?: boolean;
  onSubmit: (selectedClasses: BaseGoogleCourses[]) => void;
  onClose: () => void;
}

const GoogleClassesModal = ({
  show,
  classes,
  isLoading,
  sync = false,
  deleteClasses = false,
  onSubmit,
  onClose,
}: GoogleClassesModalProps) => {
  const { t } = useTranslation();
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const initiallySelected = classes.filter(c => c.deletionRequired === true).map(c => c.id);

    setSelectedClasses(initiallySelected);
  }, [classes]);

  const handleToggle = (id: string) => {
    setSelectedClasses(prev =>
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedClasses.length === classes.length) {
      setSelectedClasses([]);
    } else {
      setSelectedClasses(classes.map(c => c.id));
    }
  };

  const getTitle = () => {
    if (deleteClasses) return t('deleteClasses');
    if (sync) return t('syncYourData');
    return t('addYourClasses');
  };

  const getDescription = () => {
    if (deleteClasses) return t('deleteClassesDescription');
    if (sync) return t('syncDataDescription');
    return t('chooseYourClasses');
  };

  const getButtonText = () => {
    if (deleteClasses) return t('delete');
    if (sync) return t('sync');
    return t('done');
  };

  const filteredClasses = classes.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <Transition
      in={show}
      timeout={400}
      mountOnEnter
      unmountOnExit
      children={state => {
        return (
          <>
            <AppBackdrop className={classnames(classNames.backdrop, `backdrop-${state}`)} />
            <AppModal className={classNames.appModal}>
              <div className={classnames(classNames.modal, `fade-${state}`)}>
                <button
                  className={classNames.closeBtn}
                  onClick={onClose}
                  aria-label={t('close').toString()}
                >
                  <SVG src={closeIcon} />
                </button>
                <h2>{getTitle()}</h2>
                <span className={classNames.description}>{getDescription()} </span>
                <div className={classNames.coursesWrapper}>
                  <input
                    type='text'
                    placeholder={t('searchClasses') || ''}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className={classNames.searchInput}
                  />
                  <div className={classNames.classList}>
                    <label className={classNames.selectAll}>
                      <input
                        type='checkbox'
                        checked={selectedClasses.length === classes.length}
                        onChange={handleSelectAll}
                      />
                      <span>
                        {t('selectAll')} ({classes.length})
                      </span>
                    </label>

                    {filteredClasses.map(c => (
                      <label key={c.id}>
                        <input
                          type='checkbox'
                          checked={selectedClasses.includes(c.id) || c.deletionRequired === true}
                          onChange={() => handleToggle(c.id)}
                          disabled={c.deletionRequired === true}
                        />
                        <span>{c.name}</span>
                        {c.deletionRequired === true && (
                          <span className={classNames.deletedClass}>{t('noLongerAvailable')}</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
                {isLoading ? (
                  <AppLoaderCircle className='m-auto' />
                ) : (
                  <TheButton
                    className={`${classNames.doneBtn} ${deleteClasses ? classNames.deleteBtn : ''}`}
                    text={getButtonText()}
                    shouldRecolor={false}
                    emitOnClick={() =>
                      onSubmit(classes.filter(c => selectedClasses.includes(c.id)))
                    }
                    disabled={selectedClasses.length === 0}
                  />
                )}
              </div>
            </AppModal>
          </>
        );
      }}
    />
  );
};

export default GoogleClassesModal;
